/**
 * Watches the live socket of a hub tab and raises the "connection lost"
 * overlay when the hub stops answering (as opposed to an auth failure, which
 * is raised directly by the auth store).
 *
 * The socket reconnects on its own, so a short blip never surfaces: only a
 * drop that lasts longer than `DROP_GRACE_MS` shows the overlay, and a
 * reconnect hides it again.
 */
import { onBeforeUnmount, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isElectron } from '../config/runtime';
import { fallbackToCloudRoute } from '../services/hubLocalRoute';
import { useAuthStore } from '../stores/auth';
import { useConnectionLostStore } from '../stores/connectionLost';
import { useSocketStore } from '../stores/socket';

const DROP_GRACE_MS = 12000;

/** Routes that are not "inside a hub" — a dropped session there is harmless. */
const NON_HUB_PATHS = ['/home', '/servers', '/login'];

export function useConnectionLostWatcher(): void {
  if (!isElectron()) return;

  const route = useRoute();
  const authStore = useAuthStore();
  const socketStore = useSocketStore();
  const lostStore = useConnectionLostStore();

  let sawConnected = socketStore.connected;
  let dropTimer: number | null = null;

  const inHubTab = (): boolean => !route.meta.bare && !NON_HUB_PATHS.includes(route.path);

  const cancelTimer = (): void => {
    if (dropTimer !== null) {
      window.clearTimeout(dropTimer);
      dropTimer = null;
    }
  };

  watch(
    () => socketStore.connected,
    (connected) => {
      if (!inHubTab()) return;

      if (connected) {
        sawConnected = true;
        cancelTimer();
        // Back online — drop the overlay if it is up.
        lostStore.clear();
        return;
      }

      // Only a session that WAS live can be "lost".
      if (!sawConnected || !authStore.isAuthenticated) return;
      dropTimer ??= window.setTimeout(async () => {
        dropTimer = null;
        // Decision 5 (card `t_be9e0190`): a window on the LAN route whose hub
        // answered "use local" hands the session back to the tunnel and says so
        // once (`services/hubLocalRoute.ts` → `fallbackToCloudRoute`) — the tab
        // keeps working instead of dropping into the lost-session overlay. Every
        // other case keeps today's behaviour.
        if (await fallbackToCloudRoute()) return;
        lostStore.markLost('offline');
      }, DROP_GRACE_MS);
    },
  );

  onBeforeUnmount(cancelTimer);
}
